import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild} from '@angular/core';
import {VRGroupService} from '../../vrgroup.service';
import {Subject} from 'rxjs';
import {MapElement} from '../../../../shared/map/map-element.model';
import {GeometryObject} from 'geojson';
import {find} from 'lodash-es';
import { map, takeUntil } from 'rxjs/operators';
import {RunningTrain, ScheduleTrain} from '../../common/train';
import {BaseComponent} from '../../../../components/general/base/base.component';
import { AuthService } from '../../../../auth/auth.service';
import { MapService } from '../../../../shared/map/map.service';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { AlarmService } from '../../../../alarm/alarm.service';
import { TrainDependencyAlarm } from '../../../../alarm/alarm-definitions/train-dependency.alarm';

export type TabId = 'schedule' | 'info' | 'cargomanifest' | 'dependencies' | 'timeline' | 'personel';

@Component({
  selector: 'qm-vrgroup-train-info',
  templateUrl: './train-info.component.html',
  styleUrls: ['./train-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainInfoComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  /**
   * Currently selected feature
   */
  @Input()
  public currentFeature: MapElement<GeometryObject>;

  @Input()
  public openTab: TabId = 'schedule';

  @Output()
  public trainChange = new EventEmitter<RunningTrain | ScheduleTrain | null>();

  @ViewChild('titleTemplate')
  public titleTemplate: TemplateRef<void>;

  public train: RunningTrain | ScheduleTrain | null;
  public tripNumber: number;
  public date: NgbDate;
  public allTripDates: NgbDate[] = [];
  public trainConflictAlarms: TrainDependencyAlarm[];
  public shiftConflictAlarms: TrainDependencyAlarm[];
  private dependencyAlarms: TrainDependencyAlarm[];
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<boolean> = new Subject();

  /**
   * Default constructor, inject dependencies.
   * @param {VRGroupService} vrGroupService
   * @param {ChangeDetectorRef} changeDetector
   * @param {MapService} mapService
   */
  constructor(private vrGroupService: VRGroupService,
              private changeDetector: ChangeDetectorRef,
              private mapService: MapService,
              private authService: AuthService,
              private alarmService: AlarmService) {
    super();
    this.name = TrainInfoComponent.getComponentName();
  }

  private static getComponentName(): string {
    return 'TrainInfo';
  }

  /**
   * Angular event, run when view is initialized (dom is rendered etc.)
   */
  ngOnInit() {
    this.trainConflictAlarms = [];
    this.shiftConflictAlarms = [];
    this.vrGroupService.runningTrainData$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      if (this.train) {
        const res = find(data, (o) => {
          return this.train._id === o._id;
        });
        if (res) {
          this.setTrain(res);
        }
      }
    });
    this.alarmService.alarms$.pipe(
      takeUntil(this.ngUnsubscribe),
      map(alarms => alarms.filter(a =>
        (a.metadata.type === 'vrTrainConflict' || a.metadata.type === 'vrShiftConflict') && a.metadata.active)),
    ).subscribe(dependencyAlarms => {
      this.dependencyAlarms = dependencyAlarms.map(alarm => alarm.metadata as TrainDependencyAlarm);
      this.updateConflictAlarms(this.dependencyAlarms);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const previous = changes.currentFeature.currentValue?.properties;
    const current = changes.currentFeature.previousValue?.properties;

    if (current?._id !== previous?._id) {
      this.setTrain(this.currentFeature?.properties);
    }
    if (current?.tripNumber !== previous?.tripNumber) {
      this.updateTripDates();
    }
  }

  private updateConflictAlarms (alarms: TrainDependencyAlarm[]) {
    if (alarms) {
      this.trainConflictAlarms = alarms
        .filter(alarm => alarm.type === 'vrTrainConflict' &&
                         alarm.payload.tripNumber === this.train?.tripNumber?.toString() &&
                         alarm.payload.trainDate === this.train?.tripDate);
      this.shiftConflictAlarms = alarms
        .filter(alarm => alarm.type === 'vrShiftConflict' &&
                         alarm.payload.tripNumber === this.train?.tripNumber?.toString() &&
                         alarm.payload.trainDate === this.train?.tripDate);
    }
    this.changeDetector.markForCheck();
  }

  public selectCorrectTab(train) {
    if (train && train.type === 'FREIGHT') {
      return 'cargomanifest';
    }
    return 'schedule';
  }

  public datepickerDateDisabled = (date: NgbDate): boolean => {
    return this.allTripDates.length > 0 && !this.allTripDates.some(tripDate => tripDate.equals(date));
  }

  public setTrain(train: RunningTrain | ScheduleTrain): void {
    console.log('TrainInfoComponent.setTrain', train);
    this.train = train;
    if (train) {
      this.tripNumber = train.tripNumber;
      const parts = train.tripDate.split('-');
      this.date = new NgbDate(+parts[0], +parts[1], +parts[2]);
      this.updateConflictAlarms(this.dependencyAlarms);
    }
    this.changeDetector.markForCheck();
    this.trainChange.emit(this.train);
  }

  public async changeDate(date: NgbDate): Promise<void> {
    const id = `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day.toString().padStart(2, '0')}_${this.tripNumber}`;

    this.loading = true;
    let train = await this.vrGroupService.getTrainById(id);
    if (!train) {
      const response = await this.vrGroupService.getTrainSchedule(id);
      train = response?.data?.data?.[0];
    }
    this.loading = false;

    this.setTrain(train);
  }

  private async updateTripDates(): Promise<void> {
    // copy tripNumber so that it can be checked after the request;
    // we don't want to update if the train has changed while the request is pending
    const tripNumber = this.train?.tripNumber;
    if (!tripNumber) {
      return;
    }

    const dates = await this.vrGroupService.getTripDatesByTripNumber(tripNumber);
    if (this.train?.tripNumber !== tripNumber) {
      return;
    }

    this.allTripDates = dates.map(date => {
      const parts = date.split('-');
      return new NgbDate(+parts[0], +parts[1], +parts[2]);
    });
    this.changeDetector.markForCheck();
  }

  /**
   * Angular destructor hook
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public hide(): void {
    this.currentFeature = null;
    this.mapService.changeHoverFeature(null);
  }

  public hasPermission(permission: string): boolean {
    return this.authService.hasPermission(permission);
  }
}
